import { getStroke } from "perfect-freehand";
import {
  b64Vecs,
  defaultShapeSchemas,
  type TLDrawShape,
  type TLHighlightShape,
  type VecModel,
} from "@tldraw/tlschema";
import { useEditor } from "../../hooks";
import { Box, Vec, type VecLike } from "../../geometry";
import { Circle2d, Polygon2d, Polyline2d, type Geometry2d } from "../geometry2d";
import { ShapeUtil, SVGContainer, type ResizeInfo } from "../shape-util";
import { colorOf, dashArray, fillPaint, HIGHLIGHT_WIDTH, highlightColor, STROKE_WIDTH, type DashStyle, type FillStyle, type SizeStyle } from "./styles";

// Freehand strokes (draw) and translucent marker strokes (highlight). Points
// are stored as base64 segments; scaleX/scaleY stretch them after resizing.

type Stroke = TLDrawShape | TLHighlightShape;

/** All points of a stroke in shape space, with pressure in z. */
export function strokePoints(shape: Stroke): Vec[] {
  const { scaleX = 1, scaleY = 1 } = shape.props as { scaleX?: number; scaleY?: number };
  const out: Vec[] = [];
  for (const segment of shape.props.segments) {
    // Segments record whether they hold x/y (2) or x/y/pressure (3) points.
    const dim = (segment as { dim?: 2 | 3 }).dim;
    const points = segment.path ? b64Vecs.decodePoints(segment.path, dim as never) : [];
    for (const p of points) out.push(new Vec(p.x * scaleX, p.y * scaleY, p.z ?? 0.5));
  }
  return out;
}

/** SVG path of a perfect-freehand outline polygon. */
function outlinePath(outline: number[][]) {
  if (!outline.length) return "";
  const [first, ...rest] = outline;
  return `M${first[0]},${first[1]}${rest.map((p) => `L${p[0]},${p[1]}`).join("")}Z`;
}

function freehandOutline(points: Vec[], width: number, opts: { isPen: boolean; isComplete: boolean }) {
  return getStroke(
    points.map((p) => [p.x, p.y, p.z ?? 0.5]),
    {
      size: width * 1.6,
      thinning: opts.isPen ? 0.65 : 0.5,
      smoothing: 0.62,
      streamline: opts.isPen ? 0.35 : 0.5,
      simulatePressure: !opts.isPen,
      last: opts.isComplete,
    },
  );
}

const linePath = (points: VecLike[], closed: boolean) =>
  points.length ? `M${points.map((p) => `${p.x},${p.y}`).join("L")}${closed ? "Z" : ""}` : "";

function strokeGeometry(shape: Stroke, width: number): Geometry2d {
  const points = strokePoints(shape);
  if (points.length < 2) {
    const p = points[0] ?? new Vec();
    return new Circle2d({ x: p.x - width, y: p.y - width, radius: width, isFilled: true });
  }
  const props = shape.props as { isClosed?: boolean; fill?: string };
  return props.isClosed
    ? new Polygon2d({ points, isFilled: !!props.fill && props.fill !== "none" })
    : new Polyline2d({ points });
}

function resizeStroke<S extends Stroke>(shape: S, info: ResizeInfo<S>) {
  const props = shape.props as { scaleX?: number; scaleY?: number };
  return {
    id: shape.id,
    type: shape.type,
    x: info.newPoint.x,
    y: info.newPoint.y,
    props: {
      scaleX: (props.scaleX ?? 1) * info.scaleX,
      scaleY: (props.scaleY ?? 1) * info.scaleY,
    },
  } as never;
}

export class DrawShapeUtil extends ShapeUtil<TLDrawShape> {
  static override type = "draw";
  static override props = defaultShapeSchemas.draw.props;
  getDefaultProps(): TLDrawShape["props"] {
    return {
      color: "black",
      fill: "none",
      dash: "draw",
      size: "m",
      segments: [],
      isComplete: false,
      isClosed: false,
      isPen: false,
      scale: 1,
      scaleX: 1,
      scaleY: 1,
    } as TLDrawShape["props"];
  }
  width(shape: TLDrawShape) {
    return STROKE_WIDTH[shape.props.size as SizeStyle] * shape.props.scale;
  }
  getGeometry(shape: TLDrawShape) {
    return strokeGeometry(shape, this.width(shape));
  }
  override getIndicatorPath(shape: TLDrawShape) {
    return linePath(strokePoints(shape), shape.props.isClosed);
  }
  override onResize(shape: TLDrawShape, info: ResizeInfo<TLDrawShape>) {
    return resizeStroke(shape, info);
  }
  component(shape: TLDrawShape) {
    return <DrawBody shape={shape} util={this} />;
  }
}

function DrawBody({ shape, util }: { shape: TLDrawShape; util: DrawShapeUtil }) {
  const editor = useEditor();
  const points = strokePoints(shape);
  const width = util.width(shape);
  const color = colorOf(editor, shape.props.color).solid;
  const closed = shape.props.isClosed && points.length > 2;
  const fill = closed ? fillPaint(editor, shape.props.color, shape.props.fill as FillStyle) : "none";
  if (shape.props.dash === "draw") {
    const outline = freehandOutline(points, width, shape.props);
    return (
      <SVGContainer>
        {closed && fill !== "none" && <path d={linePath(points, true)} fill={fill} />}
        <path d={outlinePath(outline)} fill={color} />
      </SVGContainer>
    );
  }
  return (
    <SVGContainer>
      <path
        d={linePath(points, closed)}
        fill={fill}
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={dashArray(shape.props.dash as DashStyle, width)}
      />
    </SVGContainer>
  );
}

export type HighlightOptions = { underlayOpacity: number; overlayOpacity: number };

export class HighlightShapeUtil extends ShapeUtil<TLHighlightShape> {
  static override type = "highlight";
  static override props = defaultShapeSchemas.highlight.props;
  static options: HighlightOptions = { underlayOpacity: 0.82, overlayOpacity: 0.35 };
  /** A highlighter with different opacities (Planroom keeps plans readable underneath). */
  static configure(options: { getCustomDisplayValues?: () => Partial<HighlightOptions> }) {
    const values = { ...HighlightShapeUtil.options, ...options.getCustomDisplayValues?.() };
    return class extends HighlightShapeUtil {
      static override options = values;
    };
  }
  getDefaultProps(): TLHighlightShape["props"] {
    return {
      color: "black",
      size: "m",
      segments: [],
      isComplete: false,
      isPen: false,
      scale: 1,
      scaleX: 1,
      scaleY: 1,
    } as TLHighlightShape["props"];
  }
  width(shape: TLHighlightShape) {
    return HIGHLIGHT_WIDTH[shape.props.size as SizeStyle] * shape.props.scale;
  }
  getGeometry(shape: TLHighlightShape) {
    return strokeGeometry(shape, this.width(shape) / 2);
  }
  override getIndicatorPath(shape: TLHighlightShape) {
    return linePath(strokePoints(shape), false);
  }
  override onResize(shape: TLHighlightShape, info: ResizeInfo<TLHighlightShape>) {
    return resizeStroke(shape, info);
  }
  component(shape: TLHighlightShape) {
    return <HighlightBody shape={shape} util={this} />;
  }
}

function HighlightBody({ shape, util }: { shape: TLHighlightShape; util: HighlightShapeUtil }) {
  const editor = useEditor();
  const points = strokePoints(shape);
  // A single point still draws a round dot.
  const d = points.length === 1 ? `M${points[0].x},${points[0].y}l0.01,0` : linePath(points, false);
  const color = highlightColor(editor, shape.props.color);
  const { underlayOpacity, overlayOpacity } = (util.constructor as typeof HighlightShapeUtil).options;
  const stroke = { d, fill: "none", stroke: color, strokeWidth: util.width(shape), strokeLinecap: "round", strokeLinejoin: "round" } as const;
  return (
    <SVGContainer style={{ mixBlendMode: "multiply" }}>
      <path {...stroke} opacity={underlayOpacity} />
      <path {...stroke} opacity={overlayOpacity} />
    </SVGContainer>
  );
}

/** Bounds of a set of stroke points (used by tools while drawing). */
export const strokeBounds = (points: VecLike[]) => Box.FromPoints(points);
export const encodeStroke = (points: VecModel[]) => b64Vecs.encodePoints(points);
